import React from "react";
import PropTypes from "prop-types";
import analytics from "core/utils/analytics";
import { useQuery } from "@apollo/client";
import { TagQuery } from "arena/queries";
import TagHelmet from "./TagHelmet";
import ErrorScreen from "core/components/ErrorScreen";
import NoMatchScreen from "arena/components/NoMatchScreen";
import useScrollToTop from "core/hooks/useScrollToTop";
import { styled } from "@mui/material/styles";
// import Grid from "@mui/material/Grid";
// import Chip from "@mui/material/Chip";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import HomepageTitle from "arena/components/HomepageTitle";
import ArenaPosts from "arena/components/ArenaPosts";
import ArenaDigitalAssets from "arena/components/ArenaDigitalAssets";
import ArenaContents from "arena/components/ArenaContents";
import ArenaRedirect from "arena/components/ArenaRedirect";
import ArenaTagCard from "arena/components/ArenaTagCard";
import { useArena } from "core/components/ArenaProvider";
import NoSsr from "@mui/material/NoSsr";
import { CollectionTypesQuery } from "arena/queries";

const Root = styled("div")(({ theme }) => ({
  overflow: "hidden",
  padding: theme.spacing(0),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(0, 3),
  },
}));

export default function Tag() {
  const location = useLocation();
  const params = useParams();
  const { t } = useTranslation("arena");
  const { tagId } = params;
  const arena = useArena();

  const onCompleted = (data) => {
    if (data.tag) {
      analytics.send({
        hitType: "pageview",
        page: location.pathname,
        title: data.tag.title,
      });
    }
  };

  const { data, loading, error } = useQuery(TagQuery, {
    fetchPolicy: "cache-and-network",
    variables: {
      id: tagId,
    },
    onCompleted,
  });

  const tag = data?.tag;

  const { data: collectionTypesData } = useQuery(CollectionTypesQuery, {
    fetchPolicy: "cache-and-network",
  });
  const collectionTypes = collectionTypesData?.collectionTypes || [];

  useScrollToTop();

  if (error) {
    return <ErrorScreen error={error} />;
  }

  if (!tag && !loading) {
    return <NoMatchScreen />;
  }

  // Redirect to custom URL if tag has one and it's different from current path
  if (tag?.url && tag.url !== `/tag/${tagId}`) {
    return <ArenaRedirect url={tag.url} />;
  }

  return (
    <Root>
      {tag && <TagHelmet tag={tag} />}

      {tag?.image || tag?.html ? (
        <ArenaTagCard tagId={tagId} maxWidth="md" />
      ) : (
        <HomepageTitle title={tag?.title} />
      )}

      <NoSsr>
        <ArenaContents
          view="GRID"
          sort={"LATEST"}
          limit={12}
          title={t("contents.startedStreams")}
          type={"STREAM"}
          status={"STARTED"}
          tagIds={[tagId]}
          previewMode={arena.defaultPreviewFormat || "HORIZONTAL"}
          noMore
          loadMore
          inView
        />

        <ArenaContents
          view="GRID"
          sort={"LATEST"}
          limit={12}
          title={t("contents.pendingStreams")}
          type={"STREAM"}
          status={"PENDING"}
          tagIds={[tagId]}
          previewMode={arena.defaultPreviewFormat || "HORIZONTAL"}
          noMore
          loadMore
          inView
        />

        <ArenaPosts
          title={t("posts")}
          view="CAROUSEL"
          variant="TITLE_OUTSIDE"
          tagIds={[tagId]}
          loadMore
        />

        <ArenaDigitalAssets
          title={t("digitalAssets")}
          view="CAROUSEL"
          variant="TITLE_OUTSIDE"
          tagIds={[tagId]}
          previewMode={arena.defaultPreviewFormat || "HORIZONTAL"}
          loadMore
        />

        <ArenaContents
          view="GRID"
          sort={"LATEST"}
          limit={12}
          title={t("contents.books")}
          type={"BOOK"}
          tagIds={[tagId]}
          previewMode="VERTICAL"
          noMore
          loadMore
          inView
        />

        <ArenaContents
          view="GRID"
          sort={"LATEST"}
          limit={12}
          title={t("contents.shorts")}
          type={"SHORTY"}
          tagIds={[tagId]}
          previewMode={arena.defaultPreviewFormat || "HORIZONTAL"}
          noMore
          loadMore
          inView
        />

        <ArenaContents
          view="GRID"
          sort={"LATEST"}
          limit={12}
          title={t("contents.videos")}
          type={"VIDEO"}
          tagIds={[tagId]}
          previewMode={arena.defaultPreviewFormat || "HORIZONTAL"}
          noMore
          loadMore
          inView
        />

        {collectionTypes.map((collectionType) => (
          <ArenaContents
            key={collectionType.id}
            view="GRID"
            sort={"LATEST"}
            limit={12}
            title={collectionType.title}
            type={"COLLECTION"}
            tagIds={[tagId]}
            collectionTypeIds={[collectionType.id]}
            previewMode={arena.defaultPreviewFormat || "HORIZONTAL"}
            noMore
            loadMore
            inView
          />
        ))}

        <ArenaContents
          view="GRID"
          sort={"LATEST"}
          limit={12}
          title={t("contents.collections")}
          type={"COLLECTION"}
          tagIds={[tagId]}
          collectionTypeIdsOnlyNull
          previewMode={arena.defaultPreviewFormat || "HORIZONTAL"}
          noMore
          loadMore
          inView
        />
      </NoSsr>
    </Root>
  );
}

Tag.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      tagId: PropTypes.string,
    }),
  }),
  location: PropTypes.shape({
    hash: PropTypes.string,
    search: PropTypes.string,
    pathname: PropTypes.string.isRequired,
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
};
